import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { AgentApiService } from '../agent-api/agent-api.service';
import { TokenService } from '../token/token.service';
import { LoggerService } from '../../../services/logger-services/logger.service';
import agentApiConfig from '../../../app/agents/config/api_endpoints.json';

interface RegulationReferenceMappingConfig {
  apiBaseUrl: string;
  endpoint: string;
  sourceIndexName: string;
  destinationIndexName: string;
}

interface RegulationReferenceMappingResponseItem {
  oldRegulationId?: string;
  referenceId?: string;
  newRegulationId?: string;
}

interface RegulationReferenceMappingApiResponse {
  status?: string | number;
  message?: string;
  data?: RegulationReferenceMappingResponseItem[];
}

const REGULATIONS_AGENT_ID = 'regulations_agent';
const REFERENCE_MAPPING_ENDPOINT_KEY = 'get_regulation_id_by_reference_mapping';


@Injectable({
  providedIn: 'root'
})
export class RegulationReferenceMappingService {
  /** Session-wide cache so multiple markdown renderers do not repeat the same mapping call. */
  private readonly mappedIdCache = new Map<string, string>();
  /** In-flight observables keyed by tenant + regulation id. */
  private readonly inFlightRequests = new Map<string, Observable<string>>();

  constructor(
    private readonly agentApiService: AgentApiService,
    private readonly tokenService: TokenService,
    private readonly loggerService: LoggerService
  ) {}

  /**
   * Returns a cached mapped regulation id when available (sync lookup for link rendering).
   */
  getCachedMappedRegulationId(regulationId: string, tenantId?: string): string | undefined {
    const cacheKey = this.buildCacheKey(regulationId, tenantId);
    if (!cacheKey) {
      return undefined;
    }
    return this.mappedIdCache.get(cacheKey);
  }

  /**
   * Maps legacy regulation ID/reference ID to the latest regulation ID.
   * Returns newRegulationId when API status is 200; otherwise returns the provided referenceId or regulationId.
   * Concurrent callers for the same regulation/tenant share one HTTP request.
   */
  getMappedRegulationId(regulationId: string, referenceId?: string, tenantId?: string): Observable<string> {
    const normalizedRegulationId = (regulationId || '').trim().toUpperCase();
    const fallbackRegulationId = ((referenceId || normalizedRegulationId) || '').trim().toUpperCase();

    if (!normalizedRegulationId) {
      return of(fallbackRegulationId);
    }

    const cacheKey = this.buildCacheKey(normalizedRegulationId, tenantId);
    const cachedMappedId = this.mappedIdCache.get(cacheKey);
    if (cachedMappedId) {
      this.logMappingStep(
        'Cache hit',
        `${normalizedRegulationId}->${cachedMappedId}, durationMs=0`
      );
      return of(cachedMappedId);
    }

    const inFlightRequest = this.inFlightRequests.get(cacheKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const mappingConfig = this.getMappingConfig();
    const endpointUrl = this.buildEndpointUrl(mappingConfig);

    if (!endpointUrl) {
      this.loggerService.logError(
        '[RegulationReferenceMapping] Missing endpoint configuration',
        'getMappedRegulationId',
        undefined,
        true
      );
      return of(fallbackRegulationId);
    }

    const payload = {
      regulationId: normalizedRegulationId,
      sourceIndexName: mappingConfig.sourceIndexName,
      destinationIndexName: this.resolveDestinationIndexName(tenantId)
    };

    const apiStartedAt = performance.now();
    const token = this.tokenService.getAccessToken();
    const request$ = this.agentApiService
      .post<RegulationReferenceMappingApiResponse>(endpointUrl, payload, token)
      .pipe(
        map((response: RegulationReferenceMappingApiResponse) => {
          const durationMs = this.formatDurationMs(apiStartedAt);
          const mappedId = this.resolveRegulationIdFromResponse(response, fallbackRegulationId).toUpperCase();
          this.logMappingStep(
            'API mapped',
            `${normalizedRegulationId}->${mappedId}, durationMs=${durationMs}, ` +
              `status=${String(response?.status ?? '')}, message=${String(response?.message ?? '')}`
          );
          return mappedId;
        }),
        catchError((error) => {
          const durationMs = this.formatDurationMs(apiStartedAt);
          this.loggerService.logError(
            `[RegulationReferenceMapping] API failed | ${normalizedRegulationId}->${fallbackRegulationId}, ` +
              `durationMs=${durationMs}, ${(error as Error)?.message || error}`,
            'getMappedRegulationId',
            undefined,
            true
          );
          return of(fallbackRegulationId);
        }),
        tap((mappedId) => this.mappedIdCache.set(cacheKey, mappedId)),
        finalize(() => this.inFlightRequests.delete(cacheKey)),
        shareReplay(1)
      );

    this.inFlightRequests.set(cacheKey, request$);
    return request$;
  }

  private buildCacheKey(regulationId: string, tenantId?: string): string {
    const normalizedRegulationId = (regulationId || '').trim().toUpperCase();
    const normalizedTenantId = String(tenantId ?? '').trim();
    if (!normalizedRegulationId) {
      return '';
    }
    return `${normalizedTenantId}:${normalizedRegulationId}`;
  }

  private resolveRegulationIdFromResponse(
    response: RegulationReferenceMappingApiResponse | null | undefined,
    fallbackRegulationId: string
  ): string {
    const status = String(response?.status ?? '').trim();
    const firstMappedRecord = response?.data?.[0];
    const newRegulationId = (firstMappedRecord?.newRegulationId || '').trim();
    const mappedReferenceId = (firstMappedRecord?.referenceId || '').trim();

    if (status === '200') {
      return newRegulationId || mappedReferenceId || fallbackRegulationId;
    }

    return mappedReferenceId || fallbackRegulationId;
  }

  private getMappingConfig(): RegulationReferenceMappingConfig {
    const regulationsAgentConfig = (agentApiConfig as any)?.[REGULATIONS_AGENT_ID] || {};
    const endpointConfig =
      regulationsAgentConfig?.endpoints?.[REFERENCE_MAPPING_ENDPOINT_KEY] || {};

    return {
      apiBaseUrl:agentApiConfig.regulations_agent.regulationReferenceMappingApiBaseUrl,
      endpoint: endpointConfig?.path,
      sourceIndexName: endpointConfig?.sourceIndexName,
      destinationIndexName: endpointConfig?.destinationIndexName
    };
  }

  private buildEndpointUrl(config: RegulationReferenceMappingConfig): string {
    const baseUrl = this.removeTrailingSlashes((config.apiBaseUrl || '').trim());
    const endpointPath = this.removeLeadingSlashes((config.endpoint || '').trim());

    if (!baseUrl || !endpointPath) {
      return '';
    }

    return `${baseUrl}/${endpointPath}`;
  }

  private removeTrailingSlashes(value: string): string {
    let normalizedValue = value;
    while (normalizedValue.endsWith('/')) {
      normalizedValue = normalizedValue.slice(0, -1);
    }
    return normalizedValue;
  }

  private removeLeadingSlashes(value: string): string {
    let normalizedValue = value;
    while (normalizedValue.startsWith('/')) {
      normalizedValue = normalizedValue.slice(1);
    }
    return normalizedValue;
  }

  private resolveDestinationIndexName(tenantIdInput?: string): string {
    const tenantId = String(tenantIdInput || '').trim();

    if (!tenantId) {
      return '';
    }

    return `${tenantId}_ri_regulations_published_index`;
  }

  private logMappingStep(step: string, detail: string): void {
    this.loggerService.logInfo(
      `[RegulationReferenceMapping] ${step} | ${detail}`,
      'getMappedRegulationId',
      undefined,
      true
    );
  }

  private formatDurationMs(startedAt: number): number {
    return Math.round(performance.now() - startedAt);
  }
}
